import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, FileText, Building, Globe, Copyright, Shield, Gavel, Phone } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

interface LegalModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const LegalModal: React.FC<LegalModalProps> = ({ isOpen, onClose }) => {
  const { t, translations } = useLanguage();

  const handleClose = () => {
    onClose();
    document.body.style.overflow = 'unset';
  };

  const handleOpen = () => {
    document.body.style.overflow = 'hidden';
  };

  React.useEffect(() => {
    if (isOpen) {
      handleOpen();
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const sections = [
    {
      id: 'company',
      icon: Building,
      title: t('legal.sections.company.title'),
      content: translations.legal?.sections?.company?.content
    },
    {
      id: 'activity',
      icon: FileText,
      title: t('legal.sections.activity.title'),
      content: translations.legal?.sections?.activity?.content
    },
    {
      id: 'hosting',
      icon: Globe,
      title: t('legal.sections.hosting.title'),
      content: translations.legal?.sections?.hosting?.content
    },
    {
      id: 'intellectual',
      icon: Copyright,
      title: t('legal.sections.intellectual.title'),
      content: translations.legal?.sections?.intellectual?.content
    },
    {
      id: 'liability',
      icon: Shield,
      title: t('legal.sections.liability.title'),
      content: translations.legal?.sections?.liability?.content
    },
    {
      id: 'applicable',
      icon: Gavel,
      title: t('legal.sections.applicable.title'),
      content: translations.legal?.sections?.applicable?.content
    },
    {
      id: 'contact',
      icon: Phone,
      title: t('legal.sections.contact.title'),
      content: translations.legal?.sections?.contact?.content
    }
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[70] flex items-center justify-center p-4"
          onClick={handleClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 50 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-black to-gray-900 p-6 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white">{t('legal.title')}</h2>
              </div>
              <motion.button
                onClick={handleClose}
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                className="p-2 bg-white/20 rounded-lg text-white hover:bg-white/30 transition-colors"
              >
                <X className="w-6 h-6" />
              </motion.button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="space-y-8">
                {sections.map((section, index) => {
                  const IconComponent = section.icon;
                  
                  return (
                    <motion.div
                      key={section.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      className="bg-gray-50 rounded-xl p-6"
                    >
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="w-8 h-8 bg-red-600 rounded-lg flex items-center justify-center">
                          <IconComponent className="w-4 h-4 text-white" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">{section.title}</h3>
                      </div>
                      
                      {typeof section.content === 'string' ? (
                        <p className="text-gray-700 leading-relaxed">{section.content}</p>
                      ) : section.content && typeof section.content === 'object' ? (
                        <div className="space-y-3">
                          {Object.values(section.content).map((item, itemIndex) => (
                            <p key={itemIndex} className="text-gray-700 leading-relaxed">
                              <strong className="text-gray-900">{item.split(':')[0]}:</strong>
                              {item.substring(item.indexOf(':') + 1)}
                            </p>
                          ))}
                        </div>
                      ) : null}
                    </motion.div>
                  );
                })}
              </div>

              {/* Footer */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.8 }}
                className="text-center mt-8 pt-6 border-t border-gray-200"
              >
                <div className="text-sm text-gray-500 space-y-1">
                  <p>© {new Date().getFullYear()} Autocat S.à r.l. - Tous droits réservés</p>
                  <p>Dernière mise à jour : {new Date().toLocaleDateString()}</p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LegalModal;